import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Import all English namespace files (always bundled as fallback)
import commonEn from "./locales/en/common.json";
import authEn from "./locales/en/auth.json";
import dashboardEn from "./locales/en/dashboard.json";
import invoiceEn from "./locales/en/invoice.json";
import invoiceLineEn from "./locales/en/invoiceLine.json";
import supplierEn from "./locales/en/supplier.json";
import administrationEn from "./locales/en/administration.json";
import profileEn from "./locales/en/profile.json";
import notificationsEn from "./locales/en/notifications.json";
import validationEn from "./locales/en/validation.json";
import modalsEn from "./locales/en/modals.json";
import expensesEn from "./locales/en/expenses.json";

// Lazy-load non-English locales
// Each locale module re-exports JSON namespace files as named exports
type LocaleModule = Record<string, Record<string, string>>;
const LOCALE_IMPORTS: Record<string, () => Promise<LocaleModule>> = {
  es: () => import("./locales/es/index").then((m) => m as unknown as LocaleModule),
  fr: () => import("./locales/fr/index").then((m) => m as unknown as LocaleModule),
  de: () => import("./locales/de/index").then((m) => m as unknown as LocaleModule),
  it: () => import("./locales/it/index").then((m) => m as unknown as LocaleModule),
  pt: () => import("./locales/pt/index").then((m) => m as unknown as LocaleModule),
  nl: () => import("./locales/nl/index").then((m) => m as unknown as LocaleModule),
};

export const NAMESPACES = [
  "common",
  "auth",
  "dashboard",
  "invoice",
  "invoiceLine",
  "supplier",
  "administration",
  "profile",
  "notifications",
  "validation",
  "modals",
  "expenses",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: commonEn,
        auth: authEn,
        dashboard: dashboardEn,
        invoice: invoiceEn,
        invoiceLine: invoiceLineEn,
        supplier: supplierEn,
        administration: administrationEn,
        profile: profileEn,
        notifications: notificationsEn,
        validation: validationEn,
        modals: modalsEn,
        expenses: expensesEn,
      },
    },
    fallbackLng: "en",
    defaultNS: "common",
    ns: [...NAMESPACES],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Don't auto-detect — LanguageContext handles resolution
      order: [],
    },
  });

/**
 * Load a language's translation bundles on demand.
 * Called by LanguageContext when the effective language changes.
 */
export async function loadLanguage(lang: string): Promise<void> {
  if (lang === "en") return; // already bundled
  if (i18n.hasResourceBundle(lang, "common")) return; // already loaded

  const loader = LOCALE_IMPORTS[lang];
  if (!loader) {
    console.warn(`[i18n] No locale bundle for "${lang}"`);
    return;
  }

  const bundles = await loader();
  for (const ns of NAMESPACES) {
    if (bundles[ns]) {
      i18n.addResourceBundle(lang, ns, bundles[ns], true, true);
    }
  }
}

export default i18n;
